import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MailIcon from "@mui/icons-material/Mail";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MoreIcon from "@mui/icons-material/MoreVert";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Typography,
} from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDrawer } from "./DrawerContext";
import { AppHeaderProps } from "./types";
import "./styles/Header.scss";

// Default routes - can be overridden via props
const DEFAULT_ROUTES = {
  settings: "/account/overview",
  profile: "/profile",
  logout: "/logout",
};

// Default logo - can be overridden via props
const DEFAULT_LOGO = {
  first: "VAR",
  second: "MINER",
};

const AppHeader: React.FC<AppHeaderProps> = ({
  user = {
    name: "Shivam Kumar",
    email: "shivam@redcliffelabs.com",
    role: "Lab Technician",
    initials: "SK",
  },
  isOnline = true,
  logo,
  notificationCount = 0,
  messageCount,
  onDrawerToggle,
  routes,
  onSettingsClick,
  onProfileClick,
  onSignOutClick,
  className,
}) => {
  // Merge provided routes with defaults
  const finalRoutes = {
    settings: routes?.settings ?? DEFAULT_ROUTES.settings,
    profile: routes?.profile ?? DEFAULT_ROUTES.profile,
    logout: routes?.logout ?? DEFAULT_ROUTES.logout,
  };
  
  // Use provided logo or default
  const finalLogo = logo ?? DEFAULT_LOGO;
  
  const { isDrawerOpen, toggleDrawer } = useDrawer();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const OnlineBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: "#44b700",
      color: "#44b700",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      width: 12,
      height: 12,
      borderRadius: "50%",
    },
  }));

  const OfflineBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: "gray",
      color: "gray",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      width: 12,
      height: 12,
      borderRadius: "50%",
    },
  }));

  const handleDrawerToggle = () => {
    toggleDrawer();
    onDrawerToggle?.();
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    handleClose();
    if (onSettingsClick) {
      onSettingsClick();
    } else {
      navigate(finalRoutes.settings);
    }
  };

  const handleProfileClick = () => {
    handleClose();
    if (onProfileClick) {
      onProfileClick();
    } else {
      navigate(finalRoutes.profile);
    }
  };

  const handleSignOutClick = () => {
    handleClose();
    if (onSignOutClick) {
      onSignOutClick();
    } else {
      localStorage.clear();
      navigate(finalRoutes.logout);
    }
  };

  const menuId = "primary-account-menu";

  const getInitials = () => {
    if (user.initials) return user.initials;
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const profileList = (
    <List
      className="profile-menu-section"
      sx={{
        width: "100%",
        maxWidth: 360,
        pointerEvents: "none",
      }}
      component="div"
      aria-hidden="true"
      tabIndex={-1}
    >
      <ListItem alignItems="flex-start" className="profile-menu-item">
        <ListItemAvatar>
          {isOnline ? (
            <OnlineBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              title="Online"
              aria-label="Online status badge"
              data-testid="online-badge"
            >
              <Avatar
                sx={{ bgcolor: deepOrange[500] }}
                alt={user.name}
                title={user.name}
                src={user.avatar}
              >
                {getInitials()}
              </Avatar>
            </OnlineBadge>
          ) : (
            <OfflineBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              title="Offline"
              aria-label="Offline status badge"
              data-testid="offline-badge"
            >
              <Avatar
                sx={{ bgcolor: deepOrange[500] }}
                alt={user.name}
                title={user.name}
                src={user.avatar}
              >
                {getInitials()}
              </Avatar>
            </OfflineBadge>
          )}
        </ListItemAvatar>
        <ListItemText
          secondary={
            <React.Fragment>
              <Typography className="profile-name" component="p">
                {user.name}
              </Typography>
              <Typography className="profile-email" component="p">
                {user.email}
              </Typography>
              <Typography className="profile-role" component="p">
                Role: {user.role}
              </Typography>
            </React.Fragment>
          }
        />
      </ListItem>
    </List>
  );

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {profileList}
      <Divider />
      <MenuItem onClick={handleSettingsClick}>
        <ListItemIcon>
          <SettingsOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Settings
      </MenuItem>
      <MenuItem onClick={handleProfileClick}>
        <ListItemIcon>
          <PersonOutlineOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Profile
      </MenuItem>
      <MenuItem onClick={handleSignOutClick}>
        <ListItemIcon>
          <LogoutOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Sign out
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {messageCount !== undefined && (
        <MenuItem>
          <IconButton 
            size="large" 
            aria-label={`show ${messageCount} new mails`}
            sx={{ color: '#1e2f97' }}
          >
            <Badge badgeContent={messageCount} color="error">
              <MailIcon />
            </Badge>
          </IconButton>
          <p>Messages</p>
        </MenuItem>
      )}
      {notificationCount > 0 && (
        <MenuItem>
          <IconButton
            size="large"
            aria-label={`show ${notificationCount} new notifications`}
            sx={{ color: '#1e2f97' }}
          >
            <Badge badgeContent={notificationCount} color="error">
              <NotificationsActiveOutlined />
            </Badge>
          </IconButton>
          <p>Notifications</p>
        </MenuItem>
      )}
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-account-menu"
          aria-haspopup="true"
          sx={{ color: '#1e2f97' }}
        >
          {isOnline ? (
            <OnlineBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              title="Online"
              aria-label="Online status badge"
              data-testid="online-badge"
            >
              <AccountCircle
                titleAccess={user.name}
                aria-label="User avatar"
              />
            </OnlineBadge>
          ) : (
            <OfflineBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              title="Offline"
              aria-label="Offline status badge"
              data-testid="offline-badge"
            >
              <AccountCircle
                titleAccess={user.name}
                aria-label="User avatar"
              />
            </OfflineBadge>
          )}
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }} className={`app-header ${className || ""}`}>
      <AppBar
        sx={{
          position: "fixed",
          top: 0,
          backgroundColor: "#ffffff",
        }}
      >
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            aria-label="open drawer"
            sx={{ 
              mr: 2,
              color: '#1e2f97',
              '&:hover': {
                backgroundColor: 'rgba(30, 47, 151, 0.04)',
              }
            }}
            onClick={handleDrawerToggle}
          >
            {isDrawerOpen ? (
              <MenuOpenIcon fontSize={"large"} />
            ) : (
              <MenuIcon fontSize={"large"} />
            )}
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ display: { xs: "none", sm: "block" } }}
            data-testid="var-miner-wrapper"
          >
            <span className="var">{finalLogo.first}</span>
            <span className="miner">{finalLogo.second}</span>
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            {notificationCount > 0 && (
              <IconButton
                size="large"
                aria-label={`show ${notificationCount} new notifications`}
                sx={{
                  color: '#1e2f97',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 47, 151, 0.04)',
                  }
                }}
              >
                <Badge badgeContent={notificationCount} color="error">
                  <NotificationsActiveOutlined />
                </Badge>
              </IconButton>
            )}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              sx={{
                color: '#1e2f97',
                '&:hover': {
                  backgroundColor: 'rgba(30, 47, 151, 0.04)',
                }
              }}
            >
              {isOnline ? (
                <OnlineBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  title="Online"
                  aria-label="Online status badge"
                  data-testid="online-badge"
                >
                  <Avatar
                    sx={{
                      bgcolor: deepOrange[500],
                      width: 20,
                      height: 20,
                      p: 1,
                    }}
                    alt={user.name}
                    title={user.name}
                    src={user.avatar}
                    sizes="large"
                  >
                    {getInitials()}
                  </Avatar>
                </OnlineBadge>
              ) : (
                <OfflineBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  title="Offline"
                  aria-label="Offline status badge"
                  data-testid="offline-badge"
                >
                  <Avatar
                    sx={{
                      bgcolor: deepOrange[500],
                      width: 20,
                      height: 20,
                      p: 1,
                    }}
                    alt={user.name}
                    title={user.name}
                    src={user.avatar}
                  >
                    {getInitials()}
                  </Avatar>
                </OfflineBadge>
              )}
            </IconButton>
          </Box>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              sx={{
                color: '#1e2f97',
                '&:hover': {
                  backgroundColor: 'rgba(30, 47, 151, 0.04)',
                }
              }}
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </Box>
  );
};

export default AppHeader;

